/**
 * Live updates over Server-Sent Events (docs/07 §7.8, M-02, P-04).
 *
 * The stream is filtered server-side by the caller's permissions, so a
 * volunteer's connection only ever carries their own team's rows.
 *
 * EventSource cannot set an Authorization header, so the short-lived access
 * token goes in the query string; nginx has access logging off for this path.
 */

import { useEffect, useRef } from "react";
import { eventStreamUrl, getAccessToken } from "@/lib/api";

export type PlatformEvent =
  | { type: "isolate.created"; data: { id: string; record_id: string; team_id: string; organism_code: string | null; status: string } }
  | { type: "isolate.updated"; data: { id: string; record_id: string; changed_fields: string[] } }
  | { type: "qa_flag.raised"; data: { id: string; isolate_id: string; code: string } }
  | { type: "qa_flag.resolved"; data: { id: string; isolate_id: string; code: string } };

const EVENT_NAMES = [
  "isolate.created",
  "isolate.updated",
  "qa_flag.raised",
  "qa_flag.resolved",
] as const;

/** Reconnect backoff, so a flaky link does not hammer the server. */
const RECONNECT_BASE_MS = 2_000;
const RECONNECT_MAX_MS = 60_000;

export function useEvents(
  onEvent: (event: PlatformEvent) => void,
  enabled = true,
): void {
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!enabled) return;

    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let closed = false;

    const connect = () => {
      const token = getAccessToken();
      if (!token || closed) return;

      source = new EventSource(eventStreamUrl(token));

      source.addEventListener("open", () => {
        attempts = 0;
      });

      for (const name of EVENT_NAMES) {
        source.addEventListener(name, (message) => {
          try {
            const data = JSON.parse((message as MessageEvent<string>).data);
            handler.current({ type: name, data } as PlatformEvent);
          } catch {
            // A malformed frame is not worth taking the stream down for.
          }
        });
      }

      source.addEventListener("error", () => {
        source?.close();
        source = null;
        if (closed) return;

        attempts += 1;
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** (attempts - 1), RECONNECT_MAX_MS);
        reconnectTimer = setTimeout(connect, delay);
      });
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      source?.close();
    };
  }, [enabled]);
}
